/** Canonical offer response values returned by `/api/v1/admin/quotations-all`. */
export type OfferResponseStatus =
  | 'pending'
  | 'accepted'
  | 'partially_accepted'
  | 'rejected';

type OfferResponseSource = {
  status?: string;
  responseStatus?: string;
  items?: unknown[];
  metadata?: {
    responseStatus?: string;
    selectionType?: string;
    selectedItemIndexes?: number[];
    items?: unknown[];
  };
};

/**
 * Prefer top-level `responseStatus` from the API; fall back for older payloads.
 */
export function getOfferResponseStatus(offer: OfferResponseSource | null | undefined): OfferResponseStatus {
  if (!offer) return 'pending';

  const top = String(offer.responseStatus || '').toLowerCase();
  if (
    top === 'partially_accepted' ||
    top === 'accepted' ||
    top === 'rejected' ||
    top === 'pending'
  ) {
    return top as OfferResponseStatus;
  }

  const meta = offer.metadata || {};
  const explicit = String(meta.responseStatus || '').toLowerCase();
  const status = String(offer.status || '').toLowerCase();
  const selectionType = String(meta.selectionType || '').toLowerCase();
  const sourceItems =
    Array.isArray(meta.items) && meta.items.length > 0
      ? meta.items
      : Array.isArray(offer.items)
        ? offer.items
        : [];
  const selectedIndexes = Array.isArray(meta.selectedItemIndexes) ? meta.selectedItemIndexes : [];
  const isPartial =
    selectionType === 'partial' ||
    (sourceItems.length > 0 &&
      selectedIndexes.length > 0 &&
      selectedIndexes.length < sourceItems.length);

  if (explicit === 'rejected' || status === 'rejected' || status === 'lost') {
    return 'rejected';
  }
  if (explicit === 'partially_accepted') {
    return 'partially_accepted';
  }

  const acceptedLike =
    explicit === 'accepted' ||
    status === 'finalized' ||
    status === 'ordered' ||
    status === 'awarded';

  if (acceptedLike) {
    return isPartial ? 'partially_accepted' : 'accepted';
  }

  return 'pending';
}

export function getOfferResponseLabel(offer: OfferResponseSource & { responseLabel?: string } | null | undefined): string {
  if (offer?.responseLabel && String(offer.responseLabel).trim()) {
    return String(offer.responseLabel).trim();
  }
  switch (getOfferResponseStatus(offer)) {
    case 'partially_accepted':
      return 'Partially accepted';
    case 'accepted':
      return 'Accepted';
    case 'rejected':
      return 'Rejected';
    default:
      return 'Pending';
  }
}

export function isOfferAcceptedLike(offer: OfferResponseSource | null | undefined): boolean {
  const status = getOfferResponseStatus(offer);
  return status === 'accepted' || status === 'partially_accepted';
}

export function offerResponseBadgeClass(label: string): string {
  if (label === 'Accepted') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
  }
  if (label === 'Partially accepted') {
    return 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200';
  }
  if (label === 'Rejected') {
    return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200';
  }
  return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
}
